import { z } from 'zod';

import type { userSchema } from '~/components/forms/adminUser/validations';
import type { departmentSchema } from '~/components/forms/department/validations';
import type { idnSchema } from '~/components/forms/idn/validations';
import type { UserRoleValue } from '~/features/auth/roles';

export type SortDirection = 'asc' | 'desc';

export type ApiErrorResponse = {
  errors?: { error: string; key: string }[];
  details: string;
};

export type LoginRequest = {
  email: string;
} & {
  password: string;
  consent?: {
    id: string;
    action: string;
  }[];
};

type Consent = { accepted: boolean; at: number };

type Patient = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  language: string;
  country: string;
  consents?: Record<string, Consent>;
};

export type UserRole = UserRoleValue;

export type AdminUser = {
  id: string;
  firstName: string;
  lastName: string;
  lastLogin?: string;
  email: string;
  phone?: string;
  departments: Department[];
  mainDepartment?: Department;
  role: UserRole;
  archivedAt?: string;
};

type GeneralUserState = {
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole;
};

type AbbottUserState = GeneralUserState & {
  role: 'abbott_user' | 'abbott_admin';
};

type AdminUserState = GeneralUserState & {
  role: Exclude<UserRole, 'abbott_user' | 'abbott_admin'>;
  idnId: string;
  idnName: string;
  departmentId?: string;
  departmentName?: string;
  primaryDepartmentId?: string;
  primaryDepartmentName?: string;
};

export type LoginResponse = {
  accessToken: string;
  tokenType: string;
  transmissionRules?: {
    backOff: number;
    backOffInitial: number;
    maxEvents: number;
    rules: {
      key: string;
      values: number[];
    }[];
    version: string;
    waitSeconds: number;
  };
  include: {
    patient: Patient;
    user: AbbottUserState | AdminUserState;
  };
};

type Pagination = {
  pageSize: number;
  totalPages: number;
  page: number;
  lastSync: string;
};

export type PatientSort =
  | 'lastName'
  | 'firstName'
  | 'dateOfBirth'
  | 'phoneNumberLast4Digits'
  | 'alertsLast24hours'
  | 'lactate'
  | 'status'
  | 'createdAt'
  | 'archivedAt';

export type DepartmentSort =
  | 'careTeamId'
  | 'name'
  | 'admins'
  | 'idnName'
  | 'idnId';

export type DepartmentsArchiveSort = 'careTeamId' | 'name' | 'archivedAt';

export type IDNSort = 'name' | 'id';

export type AdminUserSort =
  | 'firstName'
  | 'lastName'
  | 'lastLogin'
  | 'email'
  | 'mainDepartment'
  | 'role';

export type UsersArchiveSort =
  | 'firstName'
  | 'lastName'
  | 'archivedAt'
  | 'email'
  | 'mainDepartment'
  | 'role';

export type AbbottAdminUsersArchiveSort =
  | 'firstName'
  | 'lastName'
  | 'archivedAt'
  | 'email'
  | 'role';

export type AdminUserFilters = {
  role?: UserRole[];
  departmentId?: string;
  idnId?: string;
  onlyArchived?: boolean;
};

export type DepartmentFilters = {
  idnId?: string;
  departmentId?: string;
  onlyArchived?: boolean;
  careTeamId?: string;
};

export type PatientsFilters = {
  departmentId?: string;
  idnId?: string;
  createdAfter?: string;
  onlyArchived?: boolean;
};

export type SearchRequest<TSort, TFilter = undefined> = {
  page: number;
  sort?: TSort;
  sortDir?: SortDirection;
  search?: string;
  filters?: TFilter;
};

export type IDN = {
  id: string;
  name: string;
  address: Address;
  phone: string;
};

export type Department = {
  id: string;
  careTeamId: string;
  name: string;
  idn: IDN;
  admins: string[];
  archivedAt?: string;
};

export type AlertSettings = {
  alertPhone: string;
  highLactateAlertAbove: number;
  highLactateAlertDuration: number;
  displayLactateData: boolean;
  highLactateAlert: boolean;
};

export type Address = {
  mainLine: string;
  secondaryLine?: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
};

export type DepartmentDetails = Department & {
  address: Address;
  phone: string;
  dateFormat: string;
  alertsSettings: AlertSettings;
};

export type SearchResponse<T> = {
  data: T[];
  totalEntries: number;
} & Pagination;

type AlertState = 'muted';

export const SENSOR_STATUS = {
  ConnectionRequest: 'connectionRequest',
  ActiveEndingSoon: 'activeEndingSoon',
  ActiveNoData: 'activeNoData',
  InactiveActivationPending: 'inactiveActivationPending',
  Active: 'active',
  ActiveWarmingUp: 'activeWarmingUp',
  InactiveSensorEnded: 'inactiveSensorEnded',
  InactiveLastData: 'inactiveLastData',
} as const;

export type SensorStatus = (typeof SENSOR_STATUS)[keyof typeof SENSOR_STATUS];

export type SensorState = {
  status: SensorStatus;
  timestamp?: string;
};

export type PatientData = {
  id: string;
  firstName: string;
  lastName: string;
  phoneNumberLast4Digits: string;
  dateOfBirth: string;
  alertsLast24hours: null | number;
  alertState?: AlertState;
  status: SensorState;
  lactate?: number;
  createdAt: string;
  archivedAt?: string;
  departments: Department[];
  email: string;
  phone: string;
  address: Address;
  mrn: string;
  idn: IDN;
  alertsSettings: AlertSettings;
};

export type PatientSearchResponse = {
  patients: PatientData[];
  totalPatients: number;
} & Pagination;

export type PatientSearchVerifyRequest = {
  phoneNumber: string;
  idnID: string;
};

export type PatientSearchVerifyResponse = {
  id: string;
  firstName: string;
  lastName: string;
  departmentId: string;
  departmentName: string;
  status: 'active' | 'inactive' | 'archived';
};

export type PatientCreateRequest = {
  patient: {
    id?: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    dateOfBirth: string;
    zipcode: string;
    idnId: string;
    departmentId: string;
    country: string;
    language: string;
    mrn: string;
    invitedBy: string;
  };
};

export type PatientSettingsUpdateRequest = {
  displayMode: 1 | 0;
  highLactateAlert: {
    enabled: boolean;
    threshold: number;
    durationInMin: number;
  };
};

export type PatientCreateResponse = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

// type Language = 'en-US';

export type AdminUserCreateRequestBody = {
  user: z.infer<typeof userSchema>;
  // & {
  // country: string;
  // language: Language;
  // idnId?: string;
  // };
};

export type AdminUserUpdateRequestBody = {
  user: AdminUserCreateRequestBody['user'] & { id: string };
};

export type AdminUserCreateRequest = z.infer<typeof userSchema>;

export type AdminUserUpdateRequest = AdminUserCreateRequest & {
  id: string;
};

export type DepartmentCreateRequest = Omit<
  z.infer<typeof departmentSchema>,
  'idnName'
>;

export type DepartmentCreateRequestBody = {
  department: Omit<z.infer<typeof departmentSchema>, 'idnName'>;
};

export type DepartmentUpdateSettingsRequest = Partial<{
  dateFormat?: DepartmentDetails['dateFormat'];
  alertsSettings?: Partial<DepartmentDetails['alertsSettings']>;
}>;

export type IDNCreateRequest = z.infer<typeof idnSchema>;

export type IDNCreateRequestBody = {
  idn: IDNCreateRequest;
};

export type UserStatusRequest = {
  token: string;
};
export type UserStatusResponse = {
  userID: string;
  status: string;
};

export type UserActivationDataRequest = {
  token: string;
  userID: string;
};

export type UserActivationDataResponse = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  role: string;
  status: string;
};

export type UserActivationUpdateRequest = {
  token: string;
  id: string;
  phoneNumber: string;
  password: string;
  privacyNoticeVersion: string;
  termsOfUseVersion: string;
};

export type AbbottUserSort =
  | 'firstName'
  | 'lastName'
  | 'lastLogin'
  | 'email'
  | 'role';
